import React, { useEffect, useState } from 'react';
import Portlet from '@ui/portlet/Portlet';
import Tabs from '@ui/tabs/Tabs';
import FormInputText from '@ui/form/input/FormInputText';
import FormButton from '@ui/button/FormButton';
import Message from '@ui/message/Message';
import { Integration, IntegrationProvider } from '@app/model/integration-settings/Integration';
import { PROVIDER_LABEL, providerOption, RESERVATIONS_SETUP_TEXT, savedCredential, TEST_CONNECTION_TEXT, TYPE_LABEL, TYPE_TEXT } from '../IntegrationSettings.utilities';
import IntegrationHeaderActions from './IntegrationHeaderActions';
import IntegrationCardFooter from './IntegrationCardFooter';
import CredentialField from './CredentialField';
import StructuredCredentialFields from './StructuredCredentialFields';
import ReservationsBackfillTab from './ReservationsBackfillTab';
import ReportRulesTab from './ReportRulesTab';
import useIntegrationCard from './useIntegrationCard';
import IntegrationRoomCatalogue from './IntegrationRoomCatalogue';
import { ApiTimezoneField, HistoryField, IntervalField, MonthsAheadField, ProviderField, StartAtField } from './IntegrationFields';

type Tab = 'SETTINGS' | 'BACKFILL' | 'RULES';

// Reservations: two tabs. Settings holds the connection and the schedule of the change checks; Backfill holds the
// manual import of the history, whose rules come from the provider (decision 20). ROO2-315 part b (Q1): a saved OPERA
// integration has a third tab, Report rules (the occupancy, ADR and Room revenue rules of the BI reports).
const ReservationsCard: React.FunctionComponent<{ integration: Integration, onOpenLog: () => void }> = ({ integration, onOpenLog }) => {
	const card = useIntegrationCard(integration);
	const [tab, setTab] = useState<Tab>('SETTINGS');
	const isSetUp = integration.status !== 'NOT_SET_UP';
	// The chosen provider decides the credential fields, the API timezone and the help lines (ROO2-296).
	const provider = card.form.watch('provider') as IntegrationProvider | '';
	const option = providerOption(integration, provider);
	const providerLabel = provider === '' ? 'provider' : PROVIDER_LABEL[provider];
	const credentialForm = option?.credentialForm;
	const fixedZone = credentialForm?.fixedSourceTimezone != null;
	// A select keeps its rule after it is hidden, so a fixed API timezone removes the rule of the hidden one.
	useEffect(() => {
		if (fixedZone) card.form.unregister('sourceTimezone');
	}, [fixedZone]);
	const saved = savedCredential(integration, provider, option);
	const credential = <>{credentialForm != null && credentialForm.fields.length > 0
		? <StructuredCredentialFields form={card.form} providerLabel={providerLabel} credentialForm={credentialForm}
				credential={saved}
				isReplacing={card.isReplacing} onReplace={card.setIsReplacing} />
		: <CredentialField form={card.form} label="Client token" providerLabel={providerLabel}
				credential={saved}
				isReplacing={card.isReplacing} onReplace={card.setIsReplacing} />}</>;

	const settings = <>
		<form className="grid grid-cols-1 gap-6 sm:grid-cols-3" autoComplete="off">
			<ProviderField form={card.form} integration={integration} />
			<FormInputText form={card.form} name="hotelCode" disabled={integration.hotelCode !== null} label={`${providerLabel} hotel code`} isRequired />
			{credential}
			{!fixedZone && <ApiTimezoneField form={card.form} />}
			<StartAtField form={card.form} />
			<HistoryField form={card.form} />
			<MonthsAheadField form={card.form} type="RESERVATIONS" />
			<div>
				<IntervalField form={card.form} label="Check for changes every" integration={integration} />
				<p className="help">{isSetUp ? `From ${card.form.watch('startAt')}, the time of the property.` : 'Change checks start after the initial backfill.'}</p>
			</div>
		</form>
		{!isSetUp && <div className="mt-6">
			<Message state="info" title="Save, fetch the room catalogue, then switch on and import history">
				{(provider === '' ? undefined : RESERVATIONS_SETUP_TEXT[provider]) ?? 'Scheduled change checks start after the initial backfill.'}
			</Message>
		</div>}
		<p className="mt-6 text-sm text-gray-500">{(provider === '' ? undefined : TEST_CONNECTION_TEXT[provider]) ?? 'Test connection reads the room types of the property.'}</p>
		<IntegrationRoomCatalogue integration={integration} isBusy={card.isBusy} isRefreshing={card.refreshRooms.isPending} isSharedRefreshing={card.catalogueRefreshing && !card.refreshRooms.isPending}
                isDirty={card.form.formState.isDirty} result={card.refreshRooms.data} error={card.refreshRooms.error}
                onRefresh={() => card.refreshRooms.mutate()} />
		<IntegrationCardFooter integration={integration} onOpenLog={onOpenLog} buttons={<>
			<FormButton state="white" onClick={card.onTest} disabled={card.isBusy}>Test connection</FormButton>
			{(card.form.formState.isDirty || !isSetUp) && <FormButton state="primary" onClick={card.onSave} disabled={card.isBusy}>Save</FormButton>}
		</>} />
	</>;

	return <div id="reservations" className="scroll-mt-6">
		<Portlet isLarge title={TYPE_LABEL.RESERVATIONS}
						actions={<IntegrationHeaderActions integration={integration} onToggle={card.onToggle} isSaving={card.isBusy} />}>
			<p className="text-sm text-gray-500">{TYPE_TEXT.RESERVATIONS} </p>
			<div className="mt-4 mb-6">
				<Tabs<Tab> value={tab} onChange={setTab} options={[
					{ label: 'Settings', value: 'SETTINGS' },
					...(isSetUp ? [{ label: 'Backfill', value: 'BACKFILL' as const }] : []),
					...(integration.reportRules !== null ? [{ label: 'Report rules', value: 'RULES' as const }] : []),
				]} />
			</div>
			{tab === 'SETTINGS' ? settings : tab === 'RULES' ? <ReportRulesTab integration={integration} /> : <ReservationsBackfillTab integration={integration} />}
		</Portlet>
	</div>;
};

export default ReservationsCard;
